import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Bot, Loader2, Send, User as UserIcon } from 'lucide-react'
import Markdown from './Markdown'
import type { CioMsg, CioStatus } from './useCioChat'

/**
 * The chat surface: transcript plus composer. Shared by the full page and the docked
 * pane, which differ only in `compact` — the pane is narrow, so it drops the suggestion
 * chips and tightens the spacing, but it is the same conversation component either way.
 *
 * Every answer carries the tools that produced it and the knowledge base version in
 * force. A number whose provenance is invisible is a number nobody can check, and this
 * agent's whole claim is that it answers from the platform rather than from memory.
 */

const SUGGESTIONS = [
  'Do we have a gold series, and how has it done since 2015?',
  'Is fund 15015 good against its peers?',
  'Does fund 15015 diversify the book in a crisis?',
  'What if equities fall 5% and volatility rises a quarter?',
]

interface Props {
  msgs: CioMsg[]
  busy: boolean
  activeTools: string[]
  status?: CioStatus
  unavailable: boolean
  onSend: (q: string) => void
  onStop: () => void
  compact?: boolean
}

export default function CioChat({
  msgs, busy, activeTools, status, unavailable, onSend, onStop, compact = false,
}: Props) {
  const [input, setInput] = useState('')
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [msgs, activeTools])

  const submit = () => {
    const q = input.trim()
    if (!q || busy || unavailable) return
    setInput('')
    onSend(q)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {unavailable && status && (
        <div className="mb-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle size={14} className="mt-0.5 flex-none" />
          <div>
            <div className="font-medium">The agent is not configured.</div>
            <div className="mt-0.5">{status.reason}</div>
          </div>
        </div>
      )}

      <div className={`min-h-0 flex-1 space-y-3 overflow-y-auto rounded-lg border border-gray-200 bg-white ${
        compact ? 'p-3' : 'p-4'}`}>
        {msgs.length === 0 && (
          <div className="space-y-3">
            <div className="text-sm text-gray-500">
              Ask about a fund, the book, market data or risk.
              {!compact && ' Try one of these:'}
            </div>
            {!compact && (
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map(q => (
                  <button key={q} disabled={busy || unavailable}
                          className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                          onClick={() => setInput(q)}>{q}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {msgs.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === 'user' ? 'justify-end' : ''}`}>
            {m.role === 'assistant' && (
              <div className="mt-0.5 flex-none rounded-full bg-blue-100 p-1.5 text-blue-700">
                <Bot size={14} />
              </div>
            )}
            <div className={`${compact ? 'max-w-[92%]' : 'max-w-[85%]'} rounded-lg px-3 py-2 text-sm ${
              m.role === 'user' ? 'bg-blue-600 text-white'
                                : 'border border-gray-200 bg-gray-50 text-gray-900'}`}>
              {m.role === 'user'
                ? <div className="whitespace-pre-wrap break-words">{m.content}</div>
                : <Markdown text={m.content} />}

              {m.streaming && !m.content && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Loader2 className="animate-spin" size={13} />Thinking…
                </div>
              )}

              {m.error && (
                <div className="mt-1.5 flex items-start gap-1.5 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">
                  <AlertTriangle size={12} className="mt-0.5 flex-none" />{m.error}
                </div>
              )}

              {m.role === 'assistant' && !!m.tools?.length && (
                <div className="mt-2 border-t border-gray-200 pt-1.5 text-[11px] text-gray-500">
                  <span className="font-medium">Tools used:</span> {m.tools.join(' → ')}
                  {m.kb && <span className="ml-1 text-gray-400">· KB {m.kb}</span>}
                </div>
              )}
            </div>
            {m.role === 'user' && (
              <div className="mt-0.5 flex-none rounded-full bg-gray-200 p-1.5 text-gray-600">
                <UserIcon size={14} />
              </div>
            )}
          </div>
        ))}

        {busy && activeTools.length > 0 && (
          <div className="flex items-center gap-2 pl-9 text-xs text-gray-500">
            <Loader2 className="animate-spin" size={13} />
            Running {activeTools[activeTools.length - 1]}
            <span className="text-gray-400">({activeTools.length} so far)</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="mt-2 flex items-end gap-2">
        <textarea
          className="min-h-[42px] flex-1 resize-y rounded-md border border-gray-300 px-3 py-2 text-sm"
          rows={1} value={input} placeholder="Ask the AI CIO…"
          disabled={busy || unavailable}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit() } }} />
        <button
          className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          disabled={busy || !input.trim() || unavailable} onClick={submit}>
          {busy ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
          {!compact && 'Send'}
        </button>
        {busy && (
          <button className="rounded-md border border-gray-300 px-2 py-2 text-xs text-gray-700 hover:bg-gray-50"
                  onClick={onStop}>Stop</button>
        )}
      </div>
    </div>
  )
}
